// src/pages/PaymentsPage.jsx

// This is the destination page for the direct payment link that accepts a paymentId prop from the url from app.jsx
// This is used for new and refill prescriptions. if it is refill, we will have a paymentId, otherwise, we will have 
// to create one first.  The productId indicates the prescription.  This happens in the component/payment.jsx.  This
// to check if a userid and a productId at a minimum are specified.  This should be a link from an email that contains
// the e.g. ?step=payment&productid=015&userid=89015 handled by app.js
// it loads the component/payment passes the props and returns to the /dashboard when finished
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useUser } from '../contexts/UserContext';
import { useOrder } from '../contexts/OrderContext';
import {
  clearPaymentAction,
  getPaymentAction,
  paymentActionMatchesOrder,
} from '../utilities/paymentAction';
import PaymentForm  from '../components/PaymentForm';
import Spinner from '../components/Spinner';
import { parseCents } from '../utilities/cents';
import { api } from '../utilities/api';

const PaymentsPage = (  ) => {
  const navigate = useNavigate();

  const {
    currentOrder,
    submitPayment,
    isLoading,
    setCurrentOrder,
    setActionOrder,
    completeRecordedPayment,
    refreshOrders,
    actionPayer,
    isActionOrderLoading,
    loadActionPayment,
  } = useOrder();
  const { user, loadingSession } = useUser();
  const [paymentAction, setPaymentAction] = useState(getPaymentAction);

  const [receiptData, setReceiptData] = useState({ pdfUrl: null, pdfFile: null });
  const [showPaymentReceipt, setShowPaymentReceipt] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPaymentInFlight, setIsPaymentInFlight] = useState(false);

  // Track if payment is complete to prevent validation errors after payment
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [paidOrder, setPaidOrder] = useState(null);

  // Track if navigation to dashboard has been triggered
  const [navigatingToDashboard, setNavigatingToDashboard] = useState(false);
  const [showRecovery, setShowRecovery] = useState(false);

  const actionMode = Boolean(paymentAction);
  const paymentUser = actionMode ? actionPayer : user;
  const paymentLoading = actionMode ? isActionOrderLoading : loadingSession;
  const hasUsableOrder = Boolean(currentOrder?.order_id);
  const orderMatchesUser = actionMode
    ? paymentActionMatchesOrder(paymentAction, currentOrder?.order_id)
    : (!user?.userid || !currentOrder?.patient_id || String(currentOrder.patient_id) === String(user.userid));
  const hasValidOrderAmounts = Number.isFinite(Number(currentOrder?.order_net ?? 0)) && Number.isFinite(Number(currentOrder?.order_shipping ?? 0));
  const recoveryReason = (() => {
    if (actionMode && !paymentActionMatchesOrder(paymentAction, currentOrder?.order_id)) return 'This payment link is invalid or expired.';
    if (!actionMode && !paymentUser?.userid) return 'Your signed-in session could not be confirmed.';
    if (!hasUsableOrder) return 'No payable order is selected for this page.';
    if (!orderMatchesUser) return 'The selected order does not match the signed-in account.';
    if (!hasValidOrderAmounts) return 'The selected order is missing payment totals.';
    return 'The payment details are incomplete.';
  })();

  // Is there nothing to pay? Answered by the server's total, not recomputed.
  //
  // This used to add `order_net * 0.035` locally, which duplicated the
  // processing rate into this repo. The rate is now per-provider and
  // administrator-editable, so a number computed here would disagree with the
  // one the server verifies against - and this particular disagreement decides
  // whether the patient is shown a payment screen at all.
  const isZeroTotal = hasUsableOrder && parseCents(currentOrder?.total_cents) !== null
    ? parseCents(currentOrder.total_cents) === 0
    : false;

  const isValidStoredOrder = (orderData) => {
    if (actionMode) return false;
    if (!orderData?.order_id) return false;
    if (!Number.isFinite(Number(orderData?.order_net ?? 0))) return false;
    if (!Number.isFinite(Number(orderData?.order_shipping ?? 0))) return false;
    if (paymentUser?.userid && orderData?.patient_id && String(orderData.patient_id) !== String(paymentUser.userid)) return false;
    return true;
  };

  const getUserPaymentAddress = () => ({
    street1: paymentUser?.billing_address1 || paymentUser?.address1 || '',
    city: paymentUser?.billing_city || paymentUser?.city || '',
    state: paymentUser?.billing_state || paymentUser?.state || '',
    zip: paymentUser?.billing_postal_code || paymentUser?.postal_code || '',
    country: 'US',
  });

  const hasZeroOrderPrerequisites = () => {
    const phone = String(paymentUser?.phone_mobile || paymentUser?.phone_home || paymentUser?.phone_work || paymentUser?.phone_work_home || '').replace(/\D/g, '');
    const address = getUserPaymentAddress();
    return Boolean(
      paymentUser?.userid
      && hasUsableOrder
      && orderMatchesUser
      && paymentUser?.email
      && (paymentUser?.first_name || paymentUser?.firstname)
      && (paymentUser?.last_name || paymentUser?.lastname)
      && phone.length >= 10
      && address.street1
      && address.city
      && address.state
      && address.zip
    );
  };

  // Payment activity must never re-run the link effect below: reloading the
  // order mid-payment remounts the payment control. The flags are read through
  // a ref so the effect re-runs only for the link itself.
  const paymentStateRef = useRef({});
  paymentStateRef.current = { isPaymentInFlight, isProcessing, paymentComplete, showPaymentReceipt };

  useEffect(() => {
    if (!actionMode) return undefined;

    let active = true;
    loadActionPayment().then(result => {
      if (!active) return;
      if (!result?.success) setShowRecovery(true);
    });

    const delay = Math.max(0, paymentAction.expiresAt - Date.now());
    const expiryTimer = window.setTimeout(() => {
      const st = paymentStateRef.current;
      if (st.isPaymentInFlight || st.isProcessing || st.paymentComplete || st.showPaymentReceipt) return;
      clearPaymentAction();
      setPaymentAction(null);
      setActionOrder({});
      setShowRecovery(true);
    }, Math.min(delay, 2147483647));

    return () => {
      active = false;
      window.clearTimeout(expiryTimer);
    };
  }, [
    actionMode,
    loadActionPayment,
    paymentAction?.expiresAt,
    setActionOrder,
  ]);

  // An order picked from the orders list, the refill flow or the localStorage
  // cache may not carry the server's quote (provider + total_cents). Fetch the
  // order once from remote_get_order so Payment always gets what that endpoint
  // returns; nothing is read from anywhere else.
  const quoteFetchedFor = useRef(null);
  useEffect(() => {
    if (actionMode || paymentComplete || showPaymentReceipt || !paymentUser?.userid) return;
    const id = currentOrder?.order_id;
    if (!id || currentOrder?.provider || quoteFetchedFor.current === id) return;
    quoteFetchedFor.current = id;
    api.getOrderDetail({ orderid: id }).then((res) => {
      if (res?.success && res.data && String(res.data.order_id) === String(id)) {
        setCurrentOrder(res.data);
      }
    });
  }, [actionMode, paymentComplete, showPaymentReceipt, paymentUser?.userid, currentOrder?.order_id, currentOrder?.provider, setCurrentOrder]);

  // useEffect to validate user and order data
  useEffect(() => {
    // Skip validation if payment is complete
    if (paymentComplete || showPaymentReceipt) {
      return;
    }
    
    // Don't show errors while data is still loading
    if (paymentLoading || isLoading) {
      return;
    }

    // Check if we have both user and order data
    if (!paymentUser?.userid) {
      setShowRecovery(true);
      return;
    }

    if (!hasUsableOrder) {
      // Check if there's a stored order in localStorage as a fallback
      const storedOrder = actionMode ? null : localStorage.getItem('redimdwm_current_order');
      if (storedOrder) {
        try {
          const parsedOrder = JSON.parse(storedOrder);
          if (isValidStoredOrder(parsedOrder)) {
            setCurrentOrder(parsedOrder);
            return;
          }
          localStorage.removeItem('redimdwm_current_order');
        } catch {
          localStorage.removeItem('redimdwm_current_order');
        }
      }

      setShowRecovery(true);
      return;
    }

    // Now that loading is complete, validate the required fields
    if (!orderMatchesUser || !hasValidOrderAmounts) {
      setShowRecovery(true);
      toast.error("Payment details are incomplete or do not match this user.");
      return;
    }
    setShowRecovery(false);
  }, [actionMode, paymentUser, currentOrder, paymentLoading, isLoading, navigate, setCurrentOrder, paymentComplete, showPaymentReceipt, hasUsableOrder, orderMatchesUser, hasValidOrderAmounts]);

  useEffect(() => {
    if (paymentComplete || showPaymentReceipt || navigatingToDashboard || (paymentUser?.userid && hasUsableOrder && orderMatchesUser && hasValidOrderAmounts)) {
      setShowRecovery(false);
      return undefined;
    }

    if (paymentLoading || isLoading) {
      setShowRecovery(false);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setShowRecovery(true);
    }, 8000);

    return () => window.clearTimeout(timer);
  }, [currentOrder, isLoading, paymentLoading, navigatingToDashboard, paymentComplete, showPaymentReceipt, paymentUser, hasUsableOrder, orderMatchesUser, hasValidOrderAmounts]);

  // Handle zero-total orders automatically
  useEffect(() => {
    if (isZeroTotal && currentOrder && paymentUser && !paymentComplete && !isProcessing) {
      handleZeroTotalOrder();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isZeroTotal, currentOrder, paymentUser, paymentComplete, isProcessing]);

  const handleZeroTotalOrder = () => {
    if (!hasZeroOrderPrerequisites()) {
      setShowRecovery(true);
      toast.error("Update address and phone before processing this order.");
      return;
    }

    const address = getUserPaymentAddress();

    setIsProcessing(true);
    setPaymentComplete(true);

    // Create mock payment data for zero-total order
    const zeroPaymentData = {
      payments: {
        amount_total: "0.00",
        servicefee: "0.00",
        date_received: new Date().toISOString(),
        date_paid: new Date().toISOString(),
        first_name: paymentUser?.first_name || paymentUser?.firstname || "",
        last_name: paymentUser?.last_name || paymentUser?.lastname || "",
        payer_email: paymentUser?.email || "",
        address_street1: address.street1,
        address_city: address.city,
        address_state: address.state,
        address_zip: address.zip,
        address_country_code: address.country,
        paypal_txn_id: `FREE_${currentOrder?.order_id}_${Date.now()}`,
        paypal_parent_txn_id: "",
        source: "Free Order",
        payment_type: "Free",
        status: "Completed"
      },
      orders: {
        order_id: currentOrder?.order_id
      }
    };

    // Process the zero payment
    submitPayment(currentOrder?.order_id, zeroPaymentData, { actionMode })
      .then(response => {
        if (response.success) {
          toast.success("Free order processed successfully!");

          // Set receipt data if available
          if (response.url || response.pdfFile) {
            setReceiptData({
              pdfUrl: response.url,
              pdfFile: response.pdfFile
            });
            setShowPaymentReceipt(true);
            setIsProcessing(false); // Stop processing when showing receipt
          } else {
            // No receipt data, navigate to dashboard immediately
            // Navigate immediately since submitPayment will clear currentOrder
                navigate(actionMode ? '/' : '/dashboard');
          }
        } else {
          throw new Error(response.error || "Failed to process free order");
        }
      })
      .catch(err => {
        toast.error(err.message || "Failed to process free order");
        setPaymentComplete(false); // Reset so user can try again
        setIsProcessing(false);
      });
  };

	const handlePaymentSuccess = (paymentData) => {
    const orderBeingPaid = currentOrder;
    setIsProcessing(true);
    // Mark payment as complete to prevent validation errors
    setPaymentComplete(true);

    // FX-179: the backend captured AND recorded this payment inside
    // /cp/orders/remote_capture_paypal_order. There is nothing to apply — this
    // only refreshes our view of the order and closes out a payment link.
    completeRecordedPayment({ actionMode })
      .then(() => {
        toast.success(`Payment applied`);

        // Show the receipt if the backend sent one back with the payment.
        if (paymentData?.url || paymentData?.pdfFile) {
          setPaidOrder(orderBeingPaid);
          setReceiptData({
            pdfUrl: paymentData.url,
            pdfFile: paymentData.pdfFile
          });
          setShowPaymentReceipt(true);
        } else {
          // No receipt data, navigate to dashboard
          setNavigatingToDashboard(true);
          navigate(actionMode ? '/' : '/dashboard');
        }
      })
      .finally(() => {
        setIsProcessing(false);
      });
	};

  const handleRefreshOrders = async () => {
    if (actionMode) {
      const result = await loadActionPayment();
      if (result?.success) {
        setShowRecovery(false);
      }
      return;
    }
    await refreshOrders();
    navigate('/orders');
  };


		const handlePaymentError = (error) => {
      if (error?.continueInitialization) return;
      if (actionMode && error?.type === 'cancelled') {
        clearPaymentAction();
        setPaymentAction(null);
        setActionOrder({});
        setShowRecovery(true);
      }
			};

  return (
    <>
      {(isProcessing || navigatingToDashboard) && (<Spinner />)}

      {!isProcessing && !isLoading && !paymentLoading && !showPaymentReceipt && (!hasUsableOrder || !paymentUser?.userid) && !navigatingToDashboard && !showRecovery && (
        <div className="text-center">
          <Spinner />
          <h1 className="text-2xl font-bold mb-4">Preparing Payment</h1>
          <p className="text-gray-500">Loading your order and account details...</p>
        </div>
      )}

      {!isProcessing && !isLoading && !paymentLoading && showRecovery && !navigatingToDashboard && (
        <div className="text-center max-w-xl mx-auto">
          <h1 className="text-2xl font-bold mb-4">Payment Details Not Loaded</h1>
          <p className="text-gray-500 mb-6">
            {recoveryReason} Open the payment link again, or choose the order from your account.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={handleRefreshOrders}
              className="px-4 py-2 bg-button text-white rounded hover:bg-button-light transition duration-200 font-semibold"
            >
              {actionMode ? 'Retry payment link' : 'Refresh orders'}
            </button>
          </div>
        </div>
      )}

      {!isProcessing && !isLoading && !paymentLoading && paymentUser?.userid && (showPaymentReceipt || (hasUsableOrder && orderMatchesUser && hasValidOrderAmounts)) && (!isZeroTotal || showPaymentReceipt) && !navigatingToDashboard && !showRecovery && (
      <PaymentForm
          user={paymentUser}
          order={showPaymentReceipt ? (paidOrder || currentOrder) : currentOrder}
          mode="order"
          showReceipt={showPaymentReceipt}
          receiptData={receiptData}
          onSuccess={handlePaymentSuccess}
          onCancel={handlePaymentError}
          actionMode={actionMode}
          onPaymentActivityChange={setIsPaymentInFlight}
      />
      )}

      {!isProcessing && !isLoading && !paymentLoading && hasUsableOrder && paymentUser?.userid && isZeroTotal && !showPaymentReceipt && !navigatingToDashboard && !showRecovery && (
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Processing Free Order</h1>
          <p className="text-gray-600 mb-4">Your order total is $0.00. Processing automatically...</p>
          <Spinner />
        </div>
      )}
    </>
  );
};

export default PaymentsPage;
